




















































































































































import { Type, ListType, isNumber, ObjectType, Types, isString, EntityType } from 'expangine-runtime';
import { LiveRuntime } from 'expangine-runtime-live';
import { System } from '@/app/SystemEvents';
import { sendNotification } from '@/app/Notify';
import { getConfirmation } from '@/app/Confirm';
import { getDataExport } from '@/app/DataExport';
import { getDataImportMapping } from '@/app/DataImport';
import { Preferences, PreferenceCategory } from '@/app/Preference';
import { TypeSettingsRecord } from '../TypeVisuals';
import { ListSubs } from './ListTypes';
import { ListObjectTableOptions } from './ListObjectTableTypes';
import ObjectFormField from '../object/ObjectFormField.vue';
import TypeInputBase from '../TypeInputBase';


const PREF_LIST_REMOVE = Preferences.define({
  key: 'list_table_remove',
  label: 'Remove from table without confirmation',
  category: [PreferenceCategory.DATA, PreferenceCategory.CONFIRM],
  defaultValue: false,
});

const PREF_LIST_CLEAR = Preferences.define({
  key: 'list_table_clear',
  label: 'Clear table without confirmation',
  category: [PreferenceCategory.DATA, PreferenceCategory.CONFIRM],
  defaultValue: false,
});

const PREF_LIST_SORT = Preferences.define({
  key: 'list_table_sort',
  label: 'Sort table without confirmation',
  category: [PreferenceCategory.DATA, PreferenceCategory.CONFIRM],
  defaultValue: false,
});


export default TypeInputBase<ListType, ListObjectTableOptions, object[], ListSubs>(Array).extend({
  name: 'ListObjectTable',
  components: {
    ObjectFormField,
  },
  data: () => ({
    pageIndex: 1,
    sortProp: null as null | string,
    sortDesc: false,
    changes: 1,
  }),
  computed: {
    itemType(): ObjectType {
      return this.type.options.item instanceof EntityType
        ? this.type.options.item.getType() as ObjectType
        : this.type.options.item as ObjectType;
    },
    itemSettings(): TypeSettingsRecord<any, any> {
      const item = this.type.options.item;

      if (item instanceof EntityType) {
        const entity = this.registry.defs.getEntity(item.options);
        if (entity) {
          return entity.meta as TypeSettingsRecord<any, any>;
        }

        return this.registry.getTypeSettings(this.itemType) as TypeSettingsRecord<any, any>;
      }

      return this.settings.sub.item as TypeSettingsRecord<any, any>;
    },
    itemName(): string {
      return this.settings.options.itemName || 'Item';
    },
    canSort(): boolean {
      return !this.settings.options.hideSort;
    },
    hasMenu(): boolean {
      return (this.canSort && (this.canSaveSort || this.canCancelSort)) || this.canAdd || this.canImport || this.canExport;
    },
    canExport(): boolean {
      return !this.settings.options.hideExport && this.isCsvType;
    },
    canImport(): boolean {
      return !this.settings.options.hideImport && this.isCsvType;
    },
    canSaveSort(): boolean {
      return !!this.sortProp;
    },
    canCancelSort(): boolean {
      return !!this.sortProp;
    },
    canRemove(): boolean {
      if (this.settings.options.hideRemove) {
        return false;
      }
      if (isNumber(this.type.options.min)) {
        return this.rowCount > this.type.options.min;
      }
      return true;
    },
    canInsert(): boolean {
      if (this.settings.options.hideInsert) {
        return false;
      }
      if (this.data) {
        return false;
      }
      return this.canAdd;
    },
    canAdd(): boolean {
      if (isNumber(this.type.options.max)) {
        return this.rowCount < this.type.options.max;
      }
      return true;
    },
    canClear(): boolean {
      return !this.readOnly;
    },
    isCsvType(): boolean {
      return !!this.csvType && this.csvType.isCompatible(this.type);
    },
    csvType(): Type | null {
      if (!this.registry) {
        return null;
      }

      const simples = this.registry.defs.getSimpleTypes();
      const simplesEnum = Types.enum(Types.many(...simples), Types.any());
      
      return Types.list(
        Types.object({
          '*': Types.many(
            Types.optional(Types.many(simplesEnum, ...simples)),
            simplesEnum,
            ...simples,
          ),
        },
      ));
    },
    rowCount(): number {
      return this.changes ? this.value.length : 0;
    },
    columns(): any[] {
      return this.settings.options.columns;
    },
    pagination(): any {
      return this.settings.options.pagination;
    },
    hasPaging(): boolean {
      return !!this.settings.options.paging && !!this.pagination;
    },
    pageSize(): number {
      return this.settings.options.pageSize || 10;
    },
    pageCount(): number {
      return Math.ceil(this.rowCount / this.pageSize);
    },
    pageStart(): number {
      return this.hasPaging
        ? Math.min(Math.min(this.pageIndex - 1, this.pageCount - 1) * this.pageSize, this.rowCount)
        : 0;
    },
    pageEnd(): number {
      return this.hasPaging
        ? Math.min(this.pageStart + this.pageSize, this.rowCount)
        : this.rowCount;
    },
    sortComparator(): false | ((a: any, b: any) => number) {
      const prop = this.sortProp;
      if (!prop) {
        return false;
      }
      const propType = this.itemType.options.props[prop];
      if (!propType) {
        return false;
      }
      const comparator = this.registry.getTypeCompare(propType);
      if (!comparator) {
        return false;
      }
      const command = LiveRuntime.getCommand(comparator);
      const multiplier = this.sortDesc ? -1 : 1;

      return (a, b) => command({ value: a[prop], test: b[prop] }) * multiplier;
    },
    sorted(): any[] {
      const comparator = this.sortComparator;

      if (!comparator) {
        return this.value;
      }
      
      const sorted = this.value.slice();
      sorted.sort(comparator);

      return sorted;
    },
    page(): any[] {
      const { sorted, pageStart, pageEnd, path } = this;

      return sorted.slice(pageStart, pageEnd)
        .map((data, index) => ({ data, path: path.concat([index + pageStart]) }))
      ;
    },
  },
  methods: {
    async clear() {
      if (!await getConfirmation({ pref: PREF_LIST_CLEAR })) {
        return;
      }

      if (this.data) {
        this.data.clear(this.path);
        // TODO query
      } else {
        this.value.splice(0, this.value.length);
        this.update();
      }
      this.changes++;
    },
    async removeAt(pageIndex: number) {
      const index = pageIndex + this.pageStart;
      if (!await getConfirmation({ pref: PREF_LIST_REMOVE })) {
        return;
      }

      if (this.data) {
        await this.data.remove(this.path.concat([index]));
        // TODO query
      } else {
        this.value.splice(index, 1);
        this.update();
      }
      this.changes++;
    },
    hasProp(prop: string): boolean {
      return prop in this.itemType.options.props;
    },
    canMove(pageIndex: number, dir: number) {
      const index = pageIndex + this.pageStart;
      const next = index + dir;

      return !this.data && next >= 0 && next < this.rowCount && !this.settings.options.hideSort;
    },
    moveTo(pageIndex: number, to: number) {
      const from = pageIndex + this.pageStart;
      const moving = this.value[from];
      this.value.splice(from, 1);
      this.value.splice(to, 0, moving);
      this.update();
      this.changes++;
    },
    move(pageIndex: number, dir: number) {
      this.moveTo(pageIndex, pageIndex + dir + this.pageStart);
    },
    insertAt(pageIndex: number) {
      const index = pageIndex + this.pageStart;
      this.value.splice(index, 0, this.itemType.fromJson(this.itemSettings.defaultValue));
      this.update();
      this.changes++;
    },
    async setItem(pageIndex: number, item: any) {
      const index = pageIndex + this.pageStart;
      if (this.data) {
        await this.data.set(this.path.concat([index]), item);
        // TODO query
      } else {
        this.$set(this.value, index, item);
      }
      this.update();
    },
    async addItem() {
      const item = this.itemType.fromJson(this.itemSettings.defaultValue);
      if (this.data) {
        await this.data.add(this.path, item);
        // TODO query
      } else {
        this.value.push(item);
        this.pageIndex = this.pageCount;
        this.update();
      }
      this.changes++;
    },
    getSortingIcon(prop: string) {
      return this.sortProp === prop
        ? this.sortDesc
          ? 'mdi-arrow-up'
          : 'mdi-arrow-down'
        : '';
    },
    sortToggle(prop: string) {
      if (!this.canSort) {
        return;
      }
      this.sortDesc = this.sortProp === prop ? !this.sortDesc : false;
      this.sortProp = this.sortProp === prop && !this.sortDesc ? null : prop;
    },
    async sortSave() {
      if (!await getConfirmation({ 
        message: 'Are you sure? The current order of elements will be the new default order of elements.',
        pref: PREF_LIST_SORT,
      })) {
        return;
      }

      this.input(this.sorted);
      this.sortCancel();
    },
    sortCancel() {
      this.sortProp = null;
      this.sortDesc = false;
    },
    exportCsv() {
      System.loadable(async () => {
        const result = await getDataExport({
          namePrefix: this.settings.options.title,
          registry: this.registry,
          data: this.value,
          type: this.itemType,
        });

        if (isString(result)) {
          sendNotification({ message: result });
        }
      });
    },
    importCsv() {
      System.loadable(async () => {
        const result = await getDataImportMapping({
          registry: this.registry,
          type: this.itemType,
          typeSettings: this.itemSettings,
        });

        if (isString(result)) {
          return sendNotification({ message: result });
        }

        switch (result.action) {
          case 'append':
            if (this.data) {
              const all: Array<Promise<void>> = [];
              for (const value of result.data) {
                all.push(this.data.add(this.path, value));
              }
              await Promise.all(all);
              // TODO query
            } else {
              this.value.push(...result.data);
            }
            break;
        case 'replace':
            if (this.data) {
              await this.data.clear(this.path);
              const all: Array<Promise<void>> = [];
              for (const value of result.data) {
                all.push(this.data.add(this.path, value));
              }
              await Promise.all(all);
              // TODO query
            } else {
              this.value.splice(0, this.value.length, ...result.data);
            }
            break;
        }

        this.update();
      });
    },
  },
});
